import { useState, useEffect, useCallback, useMemo } from 'react';
import { Product, ProductAvailability, ProductTag } from 'core/product/Product';
import {
  ColumnDefinition,
  getStatusDesColumn,
  renderColumn,
  sortableColumn,
} from 'components/TableColumn/TableColumn';
import formatters from './listFormatters';
import _ from 'lodash';
import styles from './productList.module.scss';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import i18n from 'i18n';
import { useProductStore } from 'store/productStore';

export enum ProductListColumns {
  ID = 'productId',
  NAME = 'name',
  IMAGE = 'imgLink',
  AVAILABILITY = 'availability',
  TAG = 'tag',
  CATEGORY = 'category',
  SALEPRICE = 'salePrice',
}

export enum ProductListType {
  BASIC = 'basic',
}

export type ProductListState = {
  readonly selectedProductListType: ProductListType;
};

export const useProductListModel = (
  productList: Product[],
  allProducts: Product[],
  singleSelect?: boolean,
  limitSelect?: boolean,
  availableProducts?: number,
) => {
  const { selectedProducts, changeSelectedProducts } = useProductStore();

  const availbleProducts = _.uniqBy(
    allProducts.concat(selectedProducts),
    'productId',
  );

  const preprocessProducts = (products: Product[]) => {
    return selectedProducts.map((product) => {
      if (
        !products.some(
          (selectedProduct) => selectedProduct.productId === product.productId,
        )
      ) {
        return {
          ...product,
          availability: ProductAvailability.RECELL,
        };
      }
      return product;
    });
  };

  const location = useLocation();
  const pathname = _.get(location, 'pathname');
  const [once, setOnce] = useState(false);

  const [state, setState] = useState({
    selectedProductListType: ProductListType.BASIC,
  } as ProductListState);

  const columnDefinition = (
    columnName: string,
    sortable: boolean = false,
    customLabel?: string,
  ): ColumnDefinition => ({
    ...sortableColumn(
      columnName,
      customLabel ? customLabel : `productList.labels.${columnName}`,
      sortable,
    ),
    classes: () => styles[columnName],
    headerClasses: () => styles[columnName],
  });

  const onSelect = (productId: string) => {
    const product = availbleProducts.find(
      (product) => product.productId === productId,
    );
    if (!product) {
      return;
    }

    if (selectedProducts.some((product) => product.productId === productId)) {
      if (singleSelect) {
        changeSelectedProducts(() => []);
        return;
      }

      changeSelectedProducts((prevSelectedProducts) =>
        prevSelectedProducts.filter(
          (product) => product.productId !== productId,
        ),
      );
    } else {
      if (singleSelect) {
        changeSelectedProducts(() => [product]);
        return;
      }
      if (limitSelect && selectedProducts.length >= (availableProducts ?? 25)) {
        toast.warn(
          i18n.t<string>('productList.messages.upperLimit', {
            count: availableProducts ?? 25,
          }),
        );
        return;
      }

      changeSelectedProducts((prevSelectedProducts) => [
        ...prevSelectedProducts,
        product,
      ]);
    }
  };

  const selectProductsAfterFilter = selectedProducts.filter((product) =>
    productList.some((p) => p.productId === product.productId),
  );
  const onSelectAll = () => {
    if (singleSelect) {
      return;
    }
    if (selectProductsAfterFilter.length === productList.length) {
      changeSelectedProducts((selectedProducts) =>
        selectedProducts.filter(
          (selectedProduct) =>
            !productList.find(
              (product) => product.productId === selectedProduct.productId,
            ),
        ),
      );
    } else {
      changeSelectedProducts((selectedProducts) =>
        _.uniqBy([...selectedProducts, ...productList], 'productId'),
      );
    }
  };

  const handleRemoveSelect = useCallback(() => {
    changeSelectedProducts(() => []);
  }, [changeSelectedProducts]);

  const onProductListTypeChange = (selectedProductListType: any) => {
    setState((prevState) => ({ ...prevState, selectedProductListType }));
  };

  const selectConfig = {
    singleSelect: singleSelect || false,
    limitSelect: limitSelect || false,
    productsOfCurrentConditionLength: productList.length,
    selectedProductsOfCurrentCondition: selectProductsAfterFilter.map(
      (product) => product.productId,
    ),
    hasSelectedProductsOfOtherCondition:
      selectedProducts.length > selectProductsAfterFilter.length,
  };
  const idColumn = renderColumn(
    {
      ...columnDefinition(ProductListColumns.ID),
      formatExtraData: {
        // currentUrl: match.url,
        selectedProducts,
        onSelect,
      },
    },
    formatters.nameFormatter,
    _.curry(formatters.nameHeaderFormatter)(selectConfig)(onSelectAll),
  );

  const getTagData = useCallback((tag: ProductTag) => {
    const des = tag;
    switch (tag) {
      case ProductTag.HOT:
        return {
          des,
          style: {
            backgroundColor: '#FFA953',
          },
        };
      case ProductTag.TRENDING:
        return {
          des,
          style: {
            backgroundColor: '#068D9D',
          },
        };
      case ProductTag.ROAS:
        return {
          des,
          style: {
            backgroundColor: '#736AD7',
          },
        };
      case ProductTag.HCP:
        return {
          des,
          style: {
            backgroundColor: '#12B76A',
          },
        };
      default:
        return;
    }
  }, []);

  const tagColumn = useMemo(() => {
    return getStatusDesColumn(
      renderColumn(columnDefinition(ProductListColumns.TAG)),
      (product: Product) => {
        return _.compact(product.tags.map((tag) => getTagData(tag)));
      },
    );
  }, [getTagData]);

  const basicColumns = _.compact([
    idColumn,
    renderColumn(
      columnDefinition(ProductListColumns.IMAGE),
      formatters.imageFormatter,
    ),
    renderColumn(
      columnDefinition(ProductListColumns.AVAILABILITY),
      formatters.availabilityFormatter,
    ),
    tagColumn,
    renderColumn(columnDefinition(ProductListColumns.CATEGORY)),
    renderColumn(columnDefinition(ProductListColumns.SALEPRICE)),
  ]);

  const typeColumnsMap = {
    [ProductListType.BASIC]: basicColumns,
  };

  useEffect(() => {
    const defaultSelect = () => {
      if (singleSelect) {
        return;
      }
      if (selectProductsAfterFilter.length === productList.length) {
        changeSelectedProducts((selectedProducts) =>
          selectedProducts.filter(
            (selectedProduct) =>
              !productList.find(
                (product) => product.productId === selectedProduct.productId,
              ),
          ),
        );
      } else {
        changeSelectedProducts((selectedProducts) =>
          _.uniqBy([...selectedProducts, ...productList], 'productId'),
        );
      }
    };
    if (
      (pathname?.includes('campaigns/new') || pathname?.includes('/split')) &&
      availbleProducts.length > 0
    ) {
      if (once) {
        return;
      }
      if (
        selectedProducts.length === 0 &&
        availbleProducts.every((product) =>
          product.tags.includes(ProductTag.HOT),
        )
      ) {
        setOnce(true);
        defaultSelect();
        toast.success(i18n.t<string>('productList.messages.defaultSelectHCP'));
      }
    }
  }, [availbleProducts]);
  return {
    state,
    selectedProducts,
    availbleProducts,
    preprocessProducts: () => preprocessProducts(availbleProducts),
    columns: typeColumnsMap[state.selectedProductListType],
    onProductListTypeChange,
    handleRemoveSelect,
    handleRemoveProduct: onSelect,
  };
};
